import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { AnalyticsService } from 'services';
import { ChatIcon, ClockIcon, CheckGreenCircle } from 'assets/icons';
import { DateRange } from '../hooks/use-date-range';
import { buildDateCategoryAxis } from '../utils/chart-axis';
import { buildGradientFill } from '../utils/chart-style';
import { buildRadialGaugeOptions } from '../utils/radial-gauge';
import SectionHeader from '../components/section-header';
import ChartCard from '../components/chart-card';
import KpiCard from '../components/kpi-card';

interface Props {
  range: DateRange;
}

const ChatSection = ({ range }: Props) => {
  const [loadingSnapshot, setLoadingSnapshot] = useState(true);
  const [statusSummary, setStatusSummary] = useState({ total: 0, open: 0, closed: 0 });
  const [responseTimes, setResponseTimes] = useState({
    avgFirstResponseMinutes: 0,
    avgResolutionMinutes: 0,
  });

  const [loadingHistory, setLoadingHistory] = useState(true);
  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSnapshot(true);
      try {
        const [status, times]: any[] = await Promise.all([
          AnalyticsService.chat.statusSummary({ withoutLoading: true }),
          AnalyticsService.chat.responseTimes({ withoutLoading: true }),
        ]);
        if (!cancelled) {
          setStatusSummary(status || { total: 0, open: 0, closed: 0 });
          setResponseTimes(times || { avgFirstResponseMinutes: 0, avgResolutionMinutes: 0 });
        }
      } catch (error) {
        console.error('[ChatSection] Error cargando estado/tiempos de chat', error);
      } finally {
        if (!cancelled) setLoadingSnapshot(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingHistory(true);
      try {
        const res: any = await AnalyticsService.chat.creationHistory({
          ...range,
          withoutLoading: true,
        });
        if (!cancelled) setHistory(res || []);
      } catch (error) {
        console.error('[ChatSection] Error cargando histórico de chat', error);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range.startDate, range.endDate]);

  const categories = history.map((r) => moment(r.date).format('DD/MM'));
  const resolutionRate =
    statusSummary.total > 0 ? Math.round((statusSummary.closed / statusSummary.total) * 100) : 0;

  return (
    <section className='analytics-section'>
      <SectionHeader
        title='Chat con abogado interno'
        subtitle='Solo consultas cliente-abogado interno. El % resuelto y los tiempos no dependen del filtro de fecha'
      />
      <div className='grid grid-asym'>
        <ChartCard
          title='Consultas nuevas por día'
          loading={loadingHistory}
          empty={!loadingHistory && history.length === 0}
          type='area'
          height={380}
          series={[{ name: 'Consultas', data: history.map((r) => r.count) }]}
          options={{
            chart: { toolbar: { show: false }, zoom: { enabled: false } },
            dataLabels: { enabled: false },
            stroke: { curve: 'smooth', width: 3 },
            xaxis: buildDateCategoryAxis(categories),
            ...buildGradientFill(['#0777ff']),
          }}
        />
        <div className='side-stack'>
          <KpiCard
            label='Consultas totales'
            value={statusSummary.total}
            icon={ChatIcon}
            accent='blue'
          />
          <ChartCard
            title='% resueltas'
            loading={loadingSnapshot}
            empty={false}
            type='radialBar'
            height={180}
            series={[resolutionRate]}
            options={buildRadialGaugeOptions('#49aa43', 'Resueltas')}
          />
          <div className='grid grid-kpi'>
            <KpiCard
              label='Primera respuesta'
              value={responseTimes.avgFirstResponseMinutes}
              suffix=' min'
              icon={ClockIcon}
              accent='purple'
            />
            <KpiCard
              label='Resolución'
              value={responseTimes.avgResolutionMinutes}
              suffix=' min'
              icon={CheckGreenCircle}
              accent='cyan'
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChatSection;
